import ENV from "../config/env";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogOptions {
  context?: string;
  data?: unknown;
  error?: unknown; // Accept any error type (Error, string, unknown from catch)
  // Allow additional arbitrary properties for migration/audit logging
  [key: string]: unknown;
}

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;

  private constructor() {
    this.logLevel = ENV.DEBUG.LOG_LEVEL as LogLevel;
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ["debug", "info", "warn", "error"];
    const currentLevel = levels.indexOf(this.logLevel);
    const messageLevel = levels.indexOf(level);
    return messageLevel >= currentLevel;
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    options?: LogOptions,
  ): string {
    const timestamp = new Date().toISOString();
    const context = options?.context ? `[${options.context}]` : "";
    return `${timestamp} ${level.toUpperCase()} ${context} ${message}`;
  }

  private logToConsole(
    level: LogLevel,
    message: string,
    options?: LogOptions,
  ): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, options);
    const data = options?.data;
    const error = options?.error;

    switch (level) {
      case "debug":
        console.debug(formattedMessage, data || "");
        break;
      case "info":
        console.info(formattedMessage, data || "");
        break;
      case "warn":
        console.warn(formattedMessage, data || "");
        break;
      case "error":
        console.error(formattedMessage, error || data || "");
        if (error && typeof error === "object" && "stack" in error) {
          console.error((error as Error).stack);
        }
        break;
    }
  }

  debug(message: string, options?: LogOptions): void {
    this.logToConsole("debug", message, options);
  }

  info(message: string, options?: LogOptions): void {
    this.logToConsole("info", message, options);
  }

  warn(message: string, options?: LogOptions): void {
    this.logToConsole("warn", message, options);
  }

  error(message: string, options?: LogOptions): void {
    this.logToConsole("error", message, options);
  }

  group(name: string): void {
    if (this.shouldLog("debug")) {
      console.group(name);
    }
  }

  groupEnd(): void {
    if (this.shouldLog("debug")) {
      console.groupEnd();
    }
  }

  measure<T>(name: string, fn: () => T): T {
    if (!this.shouldLog("debug")) {
      return fn();
    }

    const start = performance.now();
    try {
      return fn();
    } finally {
      const duration = performance.now() - start;
      this.debug(`${name} took ${duration.toFixed(2)}ms`);
    }
  }

  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    if (!this.shouldLog("debug")) {
      return fn();
    }

    const start = performance.now();
    try {
      return await fn();
    } finally {
      const duration = performance.now() - start;
      this.debug(`${name} took ${duration.toFixed(2)}ms`);
    }
  }
}

export const logger = Logger.getInstance();
